import React, { useMemo } from 'react';
import { getCampaignPalettesShifted } from '../../themes/palettes';
import { countdownLabel } from '../../derivations/schedule';
import { TopBar } from '../chrome/TopBar';
import { SectionHeader } from '../primitives/SectionHeader';
import { PalettePageGradient } from '../primitives/PalettePageGradient';
import { CalmCampaignHero } from './CalmCampaignHero';
import { CalmPanel } from './CalmPanel';
import { CalmCharacter } from './CalmCharacter';
import { CalmEmpty } from './CalmEmpty';
import { CalmNextSession } from './CalmNextSession';
import { CalmPresence } from './CalmPresence';
import { CalmTools } from './CalmTools';
import { CalmToolRow } from './CalmToolRow';
import { CalmNote } from './CalmNote';
import { isCampaignTestCharacter } from '../../../../campaignCharacterTargets';

export const CampaignViewV2 = ({
  theme, density, palettes,
  cardStyle, cardFinish,
  bgColor, setBgColor, themeBgColor,
  onOpenThemes,
  campaignId, onBack,
  data,
  actions,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};

  const user = data?.user || { name: '', handle: '@you', initial: '?' };
  const campaigns = data?.campaigns || [];
  const campaign = campaigns.find(cp => cp.id === campaignId);
  // Hooks must run unconditionally; null-check after they're called.
  const hueShift = campaign?.hueShift || 0;
  const campaignPalettes = useMemo(
    () => getCampaignPalettesShifted(theme, hueShift),
    [theme, hueShift]
  );
  const palette = campaignPalettes[0];

  if (!campaign) {
    return (
      <>
        <PalettePageGradient palette={palette} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <TopBar
            theme={theme} density={density} user={user}
            onOpenThemes={onOpenThemes}
            bgColor={bgColor} setBgColor={setBgColor} themeBgColor={themeBgColor}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <button
              onClick={onBack}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: 'transparent',
                border: `1px solid ${c.borderLight}`,
                color: c.textSecondary,
                padding: '6px 12px',
                borderRadius: 4,
                fontFamily: t.fontFamily,
                fontSize: '0.82rem',
                cursor: 'pointer',
              }}
            >{'<-'} Back to home</button>
            <span style={{
              fontFamily: t.fontFamily,
              fontSize: '0.68rem',
              letterSpacing: '0.18em',
              color: c.textMuted,
              textTransform: 'uppercase',
            }}>syncing</span>
          </div>
          <CalmPanel theme={theme}>
            <div role="status" style={{ fontFamily: t.fontFamily, color: c.textSecondary }}>
              Campaign is syncing. If it does not appear in a moment, return home and reopen it.
            </div>
          </CalmPanel>
        </div>
      </>
    );
  }

  const isDemoCampaign = !!campaign.isDemo;
  const canManageCampaign = !isDemoCampaign && (campaign?.role === 'owner' || campaign?.role === 'dm');
  const chars = data?.campaignCharacters?.[campaignId] || [];
  const friends = data?.campaignFriends?.[campaignId] || [];
  const showTestToggle = canManageCampaign && (campaign.totalTestCharacterCount || 0) > 0;
  const extra = data?.campaignExtra?.[campaignId] || {};
  const campaignNotes = data?.announcementsForCampaign?.[campaignId]
    || (data?.tableNotes || []).filter(n => n.campaignId === campaignId);
  const onlineCount = friends.filter(f => f.status !== 'offline').length;

  const bodyFont = t.fontFamily;
  const headingFont = t.headingFamily;

  // Section regions mirror v1's open-region treatment — top hairline,
  // breathing room, no surrounding chrome. The cards inside lift cleanly
  // against the page rather than competing with a wrapper.
  const sectionRegionStyle = {
    paddingTop: density.spacing.sectionGap,
    paddingBottom: density.spacing.sectionGap,
    marginBottom: density.spacing.cardGap,
    borderTop: `1px solid ${c.borderLight}`,
  };
  const firstSectionRegionStyle = {
    ...sectionRegionStyle,
    borderTop: 'none',
    paddingTop: 0,
  };
  const characterHeaderAction = showTestToggle || (!isDemoCampaign && chars.length > 0) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {showTestToggle && (
        <button
          type="button"
          onClick={() => actions?.onSetShowTestCharacters?.(campaignId, !campaign.showTestCharacters)}
          style={{
            background: 'transparent',
            border: `1px solid ${c.borderLight}`,
            color: c.textSecondary,
            fontSize: '0.74rem',
            cursor: 'pointer',
            padding: '4px 10px',
            borderRadius: 4,
            fontFamily: bodyFont,
          }}
        >
          {campaign.showTestCharacters ? 'Hide saved practice' : 'Show saved practice'}
        </button>
      )}
      {!isDemoCampaign && chars.length > 0 && (
        <button
          type="button"
          onClick={() => actions?.onOpenCampaignCharacterSetup?.(campaignId)}
          style={{
            background: 'transparent',
            border: `1px solid ${c.borderLight}`,
            color: c.textSecondary,
            fontSize: '0.74rem',
            cursor: 'pointer',
            padding: '4px 10px',
            borderRadius: 4,
            fontFamily: bodyFont,
          }}
        >
          Add character
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <>
      <PalettePageGradient palette={palette} />

      <div style={{ position: 'relative', zIndex: 1 }}>
      <TopBar
        theme={theme} density={density} user={user}
        onOpenThemes={onOpenThemes}
        bgColor={bgColor} setBgColor={setBgColor} themeBgColor={themeBgColor}
      />

      {/* Breadcrumb + version pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 14,
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent',
            border: `1px solid ${c.borderLight}`,
            color: c.textSecondary,
            padding: '6px 12px',
            borderRadius: 6,
            fontFamily: bodyFont,
            fontSize: '0.82rem',
            cursor: 'pointer',
          }}
        >← Back to home</button>
        <span style={{
          fontFamily: bodyFont,
          fontSize: '0.68rem',
          letterSpacing: '0.18em',
          color: c.textMuted,
          textTransform: 'uppercase',
        }}>v2 · quiet</span>
      </div>

      {/* Campaign hero strip — full width, calm panel, palette only on the
          crest tint, the campaign name, and the inline 'Tonight' pip. */}
      <CalmCampaignHero
        campaign={campaign} extra={extra} palette={palette}
        bodyFont={bodyFont} headingFont={headingFont} c={c} theme={theme}
        onEnterAsDm={() => actions?.onEnterAsCampaignDm?.(campaignId)}
        onOpenSettings={isDemoCampaign ? undefined : () => actions?.onOpenCampaignSettings?.(campaignId)}
        onCreateInvite={canManageCampaign ? () => actions?.onCreateCampaignInvite?.(campaignId) : undefined}
      />

      {/* Two-column body — same proportions as v1 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
        gap: density.spacing.cardGap,
        alignItems: 'flex-start',
      }}>
        {/* LEFT */}
        <div>
          <div style={firstSectionRegionStyle}>
            <SectionHeader
              icon="✦"
              title="My Characters"
              count={chars.length}
              theme={theme} density={density}
              color={c.textSecondary}
              action={characterHeaderAction}
            />
            <CalmPanel theme={theme}>
              {chars.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: density.spacing.itemGap }}>
                  {chars.map((ch) => {
                    const isSavedPractice = isCampaignTestCharacter(ch);
                    const characterId = ch.charId || ch.id;
                    return (
                      <CalmCharacter
                        key={ch.id}
                        char={ch} palette={palette}
                        bodyFont={bodyFont} headingFont={headingFont}
                        theme={theme}
                        onContinue={ch.isPlayable === false || isSavedPractice
                          ? undefined
                          : () => actions?.onEnterAsCampaignChar?.(campaignId, characterId)}
                        onRemove={canManageCampaign
                          ? () => actions?.onRemoveCampaignCharacter?.(campaignId, characterId, ch.name)
                          : undefined}
                      />
                    );
                  })}
                </div>
              ) : (
                <CalmEmpty
                  palette={palette} bodyFont={bodyFont}
                  headingFont={headingFont} c={c}
                  onAddCharacter={() => actions?.onOpenCampaignCharacterSetup?.(campaignId)}
                />
              )}
            </CalmPanel>
          </div>

          <div style={sectionRegionStyle}>
            <SectionHeader
              icon="✦"
              title="Announcements"
              count={campaignNotes.length}
              theme={theme} density={density}
              color={c.textSecondary}
              action={canManageCampaign ? (
                <button
                  type="button"
                  onClick={() => actions?.onOpenAnnouncementComposer?.(campaignId, null)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${c.borderLight}`,
                    color: c.textSecondary,
                    fontSize: '0.74rem',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontFamily: bodyFont,
                  }}
                >+ Compose</button>
              ) : undefined}
            />
            {campaignNotes.length === 0 ? (
              <CalmPanel theme={theme}>
                <div style={{
                  fontFamily: bodyFont,
                  fontSize: '0.88rem',
                  color: c.textMuted,
                  fontStyle: 'italic',
                  padding: '4px 0',
                }}>
                  {canManageCampaign
                    ? "Nothing posted yet. Use Compose to share something with the table."
                    : "Your DM hasn't posted anything yet."}
                </div>
              </CalmPanel>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: density.spacing.itemGap }}>
                {campaignNotes.map(n => {
                  const canManage = canManageCampaign;
                  return (
                    <CalmPanel theme={theme} key={n.id}>
                      <div style={{ position: 'relative' }}>
                        <CalmNote
                          note={n} palette={palette}
                          bodyFont={bodyFont} headingFont={headingFont} c={c}
                        />
                        {canManage && (
                          <button
                            type="button"
                            onClick={() => actions?.onOpenAnnouncementComposer?.(campaignId, n)}
                            title="Edit this announcement"
                            style={{
                              position: 'absolute',
                              top: 0, right: 0,
                              background: 'transparent',
                              border: `1px solid ${c.borderLight}`,
                              color: c.textSecondary,
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: '0.72rem',
                              cursor: 'pointer',
                              fontFamily: bodyFont,
                            }}
                          >Edit</button>
                        )}
                      </div>
                    </CalmPanel>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div>
          <div style={firstSectionRegionStyle}>
            <SectionHeader
              icon="✦" title="Next Session"
              theme={theme} density={density}
              color={c.textSecondary}
            />
            <CalmPanel theme={theme} accent={countdownLabel(campaign.schedule) === 'Tonight' ? palette : null} padding="18px 20px 18px 27px">
              <CalmNextSession
                schedule={campaign.schedule}
                palette={palette}
                bodyFont={bodyFont} headingFont={headingFont} c={c}
              />
            </CalmPanel>
          </div>

          {friends.length > 0 && (
            <div style={sectionRegionStyle}>
              <SectionHeader
                icon="✦" title="Around the Table"
                theme={theme} density={density}
                color={c.textSecondary}
                action={
                  <span style={{
                    fontFamily: bodyFont,
                    fontSize: '0.72rem',
                    color: c.textMuted,
                    letterSpacing: '0.02em',
                  }}>{onlineCount} of {friends.length} online</span>
                }
              />
              <CalmPanel theme={theme}>
                <CalmPresence friends={friends} bodyFont={bodyFont} c={c} />
              </CalmPanel>
            </div>
          )}

          {canManageCampaign && (
            <div style={sectionRegionStyle}>
              <SectionHeader
                icon="*" title="Sandbox"
                theme={theme} density={density}
                color={c.textSecondary}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <CalmToolRow
                  tool={{
                    key: 'sandbox-open',
                    label: 'Open / resume sandbox',
                    sub: 'local practice campaign',
                    icon: '*',
                    onClick: () => actions?.onOpenCampaignSandbox?.(campaignId),
                  }}
                  palette={palette}
                  bodyFont={bodyFont}
                  c={c}
                />
                <CalmToolRow
                  tool={{
                    key: 'sandbox-reseed',
                    label: 'Reset and reseed sandbox',
                    sub: 'replace local sandbox seed data',
                    icon: '*',
                    onClick: () => actions?.onResetCampaignSandbox?.(campaignId),
                  }}
                  palette={palette}
                  bodyFont={bodyFont}
                  c={c}
                />
                <CalmToolRow
                  tool={{
                    key: 'sandbox-sync-from-campaign',
                    label: 'Sync campaign to sandbox',
                    sub: 'refresh sandbox from current campaign setup',
                    icon: '*',
                    onClick: () => actions?.onSyncCampaignSandbox?.(campaignId),
                  }}
                  palette={palette}
                  bodyFont={bodyFont}
                  c={c}
                />
              </div>
            </div>
          )}

          {!isDemoCampaign && (
            <div style={sectionRegionStyle}>
              <SectionHeader
                icon="✦" title="Campaign Tools"
                theme={theme} density={density}
                color={c.textSecondary}
              />
              <CalmTools
                palette={palette} bodyFont={bodyFont} c={c}
                campaign={campaign}
                onOpenPartyJournal={() => actions?.onOpenPartyJournal?.(campaignId)}
                onOpenWorldLore={() => actions?.onOpenWorldLore?.(campaignId)}
                onOpenCampaignModule={(viewName) => actions?.onOpenCampaignModule?.(campaignId, viewName)}
              />
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
};
